import { cloneElement, useMemo } from 'react'

import { Button, type ButtonProps } from '@mui/material'
import { MUI_STANDARD_TRANSITION_DURATION } from '@/constants/mui'
import { useUnmountingDelay } from '@/hooks/useUnmountingDelay'

import type { DialogHandlerProps, DialogLike } from './dialogHandlers.types'

type Props<ChildrenProps extends DialogLike> = DialogHandlerProps<ChildrenProps> & {
  ButtonProps?: ButtonProps
  unmountDelay?: number
}

export const OpenDialogButton = <ChildrenProps extends DialogLike>({
  children,
  label,
  ButtonProps,
  unmountDelay = MUI_STANDARD_TRANSITION_DURATION
}: Props<ChildrenProps>) => {
  const { isOpen, isMounted, open, close } = useUnmountingDelay(false, unmountDelay)
  const { onClick, ...rest } = ButtonProps || {}

  const dialog = useMemo(
    () =>
      cloneElement(children, {
        ...children.props,
        open: isOpen,
        onClose: () => {
          children.props?.onClose?.()
          close()
        }
      }),
    [children, isOpen, close, children.props]
  )

  return (
    <>
      <Button
        onClick={(e) => {
          open()
          onClick?.(e)
        }}
        {...rest}
      >
        {label}
      </Button>
      {isMounted && dialog}
    </>
  )
}
